/**
 * #/settings — idioma, sesión, a qué API apunta este navegador y con qué token.
 *
 * El test de conexión pega `GET /api/health`, que el API deja SIEMPRE abierto
 * (lo consultan monitoreo y balanceadores). Por eso un health en verde con
 * token vacío no garantiza acceso: se avisa y se ofrece probar un endpoint
 * protegido (`/api/matches`), que es el que realmente valida el Bearer.
 *
 * El campo del Bearer NO se fue con la llegada del login: es el token único de
 * la federación (`SCOUT_API_TOKEN`) y queda como MODO DE COMPATIBILIDAD para
 * integraciones que no tienen usuario. Lo que sí cambió es que ahora está
 * rotulado como tal y que, si hay sesión iniciada, la sesión manda.
 */
import { useState } from 'react';
import { Card } from '../components/States';
import { LOCALES, LOCALE_NAMES, isLocale } from '../i18n/index';
import { useT } from '../i18n/use-t';
import type { ApiClient, Identity } from '../lib/api-client';
import { ApiError } from '../lib/api-client';
import { roleLabel, viaLabel, type PortalSession } from '../lib/auth';
import {
  EFFICIENCY_PRESETS,
  EFFICIENCY_PRESET_IDS,
  isEfficiencyPresetId,
} from '../lib/efficiency';
import { stripTrailingSlash, type Settings } from '../lib/settings';
import { THEMES, isTheme } from '../lib/theme';

type TestState =
  | { kind: 'idle' }
  | { kind: 'testing' }
  | { kind: 'ok'; message: string }
  | { kind: 'fail'; message: string };

export function SettingsPage({
  client,
  settings,
  onSave,
  session,
  identity,
  onLogout,
}: {
  client: ApiClient;
  settings: Settings;
  onSave: (next: Settings) => void;
  /** Sesión guardada en este navegador (`null` = nadie ingresó). */
  session: PortalSession | null;
  /** Identidad confirmada por el API; puede existir sin sesión (token/dev). */
  identity: Identity | null;
  onLogout: () => void;
}): JSX.Element {
  const [apiUrl, setApiUrl] = useState(settings.apiUrl);
  const [apiToken, setApiToken] = useState(settings.apiToken);
  const [replayUrl, setReplayUrl] = useState(settings.replayUrl);
  const [replayToken, setReplayToken] = useState(settings.replayToken);
  const [test, setTest] = useState<TestState>({ kind: 'idle' });
  const [saved, setSaved] = useState(false);
  const t = useT();

  const dirty = stripTrailingSlash(apiUrl.trim()) !== settings.apiUrl || apiToken.trim() !== settings.apiToken;
  const replayDirty =
    stripTrailingSlash(replayUrl.trim()) !== settings.replayUrl ||
    replayToken.trim() !== settings.replayToken;

  /** Guarda SIEMPRE todos los campos: un save parcial borraría los otros. */
  const saveAll = (patch: Partial<Settings>): void => {
    onSave({
      apiUrl,
      apiToken,
      replayUrl,
      replayToken,
      locale: settings.locale,
      efficiencyPreset: settings.efficiencyPreset,
      theme: settings.theme,
      ...patch,
    });
  };

  const save = (): void => {
    saveAll({});
    setSaved(true);
    setTest({ kind: 'idle' });
  };

  /** El VAR se guarda por su cuenta: es config de la cancha, no del backend. */
  const saveReplay = (): void => {
    saveAll({});
    setSaved(true);
  };

  /**
   * El idioma NO espera al botón Guardar: se aplica y se persiste al instante,
   * porque su efecto es justamente que la pantalla cambie mientras se la mira.
   */
  const changeLocale = (value: string): void => {
    if (!isLocale(value)) return;
    saveAll({ locale: value });
  };

  /** El diseño, igual que el idioma: se ve el cambio mientras se lo elige. */
  const changeTheme = (value: string): void => {
    if (!isTheme(value)) return;
    saveAll({ theme: value });
  };

  /**
   * La convención tampoco espera al botón Guardar: cambia lo que TODAS las
   * pestañas de estadística están mostrando (mismo criterio que el idioma).
   */
  const changeEfficiency = (value: string): void => {
    if (!isEfficiencyPresetId(value)) return;
    saveAll({ efficiencyPreset: value });
  };

  /**
   * Salir: revoca el token en el server y borra la sesión local. Si el server
   * no contesta, igual se borra local — quedarse "adentro" en pantalla con una
   * credencial que ya no sirve es peor que un token huérfano.
   */
  const logout = async (): Promise<void> => {
    try {
      await client.logout();
    } catch {
      /* no-op */
    } finally {
      onLogout();
    }
  };

  const runTest = async (): Promise<void> => {
    setTest({ kind: 'testing' });
    try {
      const health = await client.health();
      // El health está abierto: para saber si el token sirve hay que pegarle a
      // un endpoint protegido.
      try {
        const matches = await client.listMatches();
        setTest({
          kind: 'ok',
          message: t('settings.testOk', { version: health.version, count: matches.length }),
        });
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'unauthorized') {
          setTest({
            kind: 'fail',
            message: t('settings.testBadToken', { version: health.version }),
          });
          return;
        }
        setTest({
          kind: 'ok',
          message: t('settings.testPartial', {
            version: health.version,
            reason: err instanceof Error ? err.message : String(err),
          }),
        });
      }
    } catch (err) {
      setTest({ kind: 'fail', message: err instanceof Error ? err.message : String(err) });
    }
  };

  return (
    <div>
      <div className="page-head">
        <h1>{t('settings.title')}</h1>
      </div>

      <Card title={t('settings.themeCard')}>
        <label className="field">
          <span>{t('settings.themeLabel')}</span>
          <select value={settings.theme} onChange={(e) => changeTheme(e.target.value)}>
            {THEMES.map((theme) => (
              <option key={theme} value={theme}>
                {theme === 'var' ? t('settings.themeVar') : t('settings.themeClasico')}
              </option>
            ))}
          </select>
        </label>
      </Card>

      <Card title={t('settings.languageCard')}>
        <label className="field">
          <span>{t('settings.languageLabel')}</span>
          <select value={settings.locale} onChange={(e) => changeLocale(e.target.value)}>
            {LOCALES.map((locale) => (
              <option key={locale} value={locale}>
                {LOCALE_NAMES[locale]}
              </option>
            ))}
          </select>
        </label>
        <p className="hint">{t('settings.languageHint')}</p>
      </Card>

      <Card title={t('settings.efficiencyCard')}>
        <p className="hint">{t('settings.efficiencyIntro')}</p>
        <label className="field">
          <span>{t('settings.efficiencyLabel')}</span>
          <select
            value={settings.efficiencyPreset}
            onChange={(e) => changeEfficiency(e.target.value)}
          >
            {EFFICIENCY_PRESET_IDS.map((id) => (
              <option key={id} value={id}>
                {EFFICIENCY_PRESETS[id]?.name ?? id}
              </option>
            ))}
          </select>
        </label>
        <p className="hint">{t('settings.efficiencyHint')}</p>
      </Card>

      <Card title={t('settings.sessionCard')}>
        {identity !== null && session !== null ? (
          <p>
            {t('settings.sessionActive', {
              name: identity.name,
              role: roleLabel(identity.role, t),
            })}
          </p>
        ) : null}
        {identity !== null && session === null ? (
          <p>
            {t('settings.sessionUsingToken')} <span className="hint">({viaLabel(identity.via, t)})</span>
          </p>
        ) : null}
        {identity === null ? <p>{t('settings.sessionNone')}</p> : null}
        <div className="row">
          {session === null ? (
            <a className="nav-link" href="#/login">
              {t('settings.sessionLogin')}
            </a>
          ) : (
            <button type="button" onClick={() => void logout()}>
              {t('settings.sessionLogout')}
            </button>
          )}
        </div>
      </Card>

      <Card title={t('settings.connectionCard')}>
        <label className="field">
          <span>{t('settings.apiUrl')}</span>
          <input
            value={apiUrl}
            onChange={(e) => {
              setApiUrl(e.target.value);
              setSaved(false);
            }}
            placeholder="https://api.liga.org"
            inputMode="url"
          />
        </label>

        <label className="field">
          <span>{t('settings.token')}</span>
          <input
            type="password"
            value={apiToken}
            onChange={(e) => {
              setApiToken(e.target.value);
              setSaved(false);
            }}
            placeholder="••••••••"
            autoComplete="off"
          />
        </label>
        <p className="hint">{t('settings.tokenHint')}</p>

        <div className="row">
          <button type="button" className="primary" onClick={save} disabled={!dirty}>
            {t('common.save')}
          </button>
          <button type="button" onClick={() => void runTest()} disabled={test.kind === 'testing'}>
            {test.kind === 'testing' ? t('settings.testing') : t('settings.testConnection')}
          </button>
          {saved ? <span className="chip ok">{t('common.saved')}</span> : null}
        </div>

        {test.kind === 'ok' ? (
          <p className="chip ok" style={{ marginTop: 12 }}>
            {test.message}
          </p>
        ) : null}
        {test.kind === 'fail' ? (
          <p className="chip bad" style={{ marginTop: 12 }}>
            {test.message}
          </p>
        ) : null}

        <p className="hint" style={{ marginTop: 12 }}>
          {t('settings.savedHint')}
        </p>
      </Card>

      <Card title={t('settings.replayCard')}>
        <p className="hint">{t('settings.replayIntro')}</p>
        <label className="field">
          <span>{t('settings.replayUrl')}</span>
          <input
            value={replayUrl}
            onChange={(e) => {
              setReplayUrl(e.target.value);
              setSaved(false);
            }}
            placeholder="http://192.168.1.50:8080"
            inputMode="url"
          />
        </label>
        <label className="field">
          <span>{t('settings.replayToken')}</span>
          <input
            type="password"
            value={replayToken}
            onChange={(e) => {
              setReplayToken(e.target.value);
              setSaved(false);
            }}
            placeholder="••••••••"
            autoComplete="off"
          />
        </label>
        <div className="row">
          <button type="button" className="primary" onClick={saveReplay} disabled={!replayDirty}>
            {t('common.save')}
          </button>
        </div>
        <p className="hint" style={{ marginTop: 12 }}>
          {t('settings.replayHint')}
        </p>
      </Card>

      <Card title={t('settings.howCard')}>
        <ul className="hint" style={{ paddingLeft: 18 }}>
          <li>{t('settings.howLocal')}</li>
          <li>
            {t('settings.howLiveBefore')}
            <code>fetch</code>
            {t('settings.howLiveMiddle')}
            <code> EventSource</code>
            {t('settings.howLiveAfter')}
          </li>
          <li>
            {t('settings.howCorsBefore')}
            <code>SCOUT_CORS_ORIGIN</code>
            {t('settings.howCorsAfter')}
          </li>
        </ul>
      </Card>
    </div>
  );
}
